import { useContext, useEffect, useState, Suspense, lazy } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import Spinner from '../Components/Progress/Spinner'
import { AuthContext } from '../Context/AuthContext'
import { mobileCheck } from '../Global/Functions'
import BatchLinks from '../Pages/DeepLinks/BatchLinks'
import AuthLinks from '../Pages/DeepLinks/AuthLinks'
import ProfileLinks from '../Pages/DeepLinks/ProfileLinks'
import BatchContextProvider from '../Context/BatchContext'
import AgoraContextProvider from '../Context/AgoraContext'
import CommandContextProvider from '../Context/CommandContext'
import HelpAndSupport from '../Pages/HelpSupport/HelpAndSupport'
import ApproveTnCDialog from '../Components/Dialogs/ApproveTnCDialog'

const MobileLanding = lazy(() =>
  import('../Pages/Mobile/MobileLanding/MobileLanding'),
)
const MobileLogin = lazy(() =>
  import('../Pages/UserManagement/MobileUserManagement/MobileLogin'),
)
const Auth = lazy(() => import('../Pages/UserManagement/Auth'))
const Dashboard = lazy(() => import('../Pages/Dashboard'))
const Privacy = lazy(() => import('../Pages/PrivacyTermsConditions/Privacy'))
const TermsConditions = lazy(() =>
  import('../Pages/PrivacyTermsConditions/TermsConditions'),
)
const ValidationSuccessful = lazy(() =>
  import('../Pages/UserManagement/ValidateEmail/ValidationSuccessful'),
)
const ErrorCodePage = lazy(() => import('../Pages/ErrorPages/ErrorCodePage'))
const Download = lazy(() => import('../Pages/Download/Download'))
const MobileRegister = lazy(() =>
  import('../Pages/UserManagement/MobileUserManagement/MobileRegister'),
)
const RegisterSuccessFul = lazy(() =>
  import('../Pages/RegisterSuccessful/RegisterSuccessFul'),
)
const PaymentComplete = lazy(() => import('../Pages/Wallet/PaymentComplete'))

const ProtectedRoute = ({ isLoggedIn, loading, children }) => {
  const location = useLocation()
  if (loading) return <Spinner />
  if (!isLoggedIn) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }
  return children
}

const AppEntry = () => {
  const { isLoggedIn, loading, authState, profileLoaded } =
    useContext(AuthContext)
  const { terms_accepted_datetime } = authState

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route
            path="/"
            element={
              isLoggedIn ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/auth/login" replace />
              )
            }
          />

          <Route
            path="/auth/email-verified"
            element={<ValidationSuccessful />}
          />
          <Route path="/auth/:action/:subaction" element={<Auth />} />
          <Route path="/auth/:action" element={<Auth />} />
          <Route path="/auth" element={<Auth />} />

          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<TermsConditions />} />

          <Route path="/register-successful" element={<RegisterSuccessFul />} />

          <Route path="/payment/complete" element={<PaymentComplete />} />
          <Route path="/app/auth/:type" element={<AuthLinks />} />
          <Route path="/app/profile/:type" element={<ProfileLinks />} />

          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <CommandContextProvider>
                  <AgoraContextProvider>
                    <Dashboard />
                  </AgoraContextProvider>
                </CommandContextProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/batch/:batchId/:type/:classId/:action"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <BatchContextProvider>
                  <BatchLinks />
                </BatchContextProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/batch/:batchId/:type/:action"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <BatchContextProvider>
                  <BatchLinks />
                </BatchContextProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/batch/:batchId/:type/:action/:classId"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <BatchContextProvider>
                  <BatchLinks />
                </BatchContextProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/batch/:batchId/:type/:action/:classId/:token"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <BatchContextProvider>
                  <BatchLinks />
                </BatchContextProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/batch/:batchId/:type"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <BatchContextProvider>
                  <BatchLinks />
                </BatchContextProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/app/batch/:batchId"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <BatchContextProvider>
                  <BatchLinks />
                </BatchContextProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/internal-error"
            element={<ErrorCodePage errCodeReceived={500} />}
          />
          <Route path="/help-support" element={<HelpAndSupport />} />
          <Route path="/download" element={<Download />} />
          <Route path="*" element={<ErrorCodePage errCodeReceived={404} />} />
        </Routes>

        <ApproveTnCDialog
          open={
            isLoggedIn &&
            !loading &&
            profileLoaded && // Ensure profile is fully loaded
            authState.role && // Ensure role is loaded
            (terms_accepted_datetime === undefined ||
              terms_accepted_datetime === null) // Show when we don't know or explicitly null
          }
          close={() => {}}
          isTutorFlow={authState.role === 'T'}
        />
      </Suspense>
    </>
  )
}

export default AppEntry
